import { RowWrapper } from '@dealstack/frontend/shared/components/DynamicResponsibleTable/RowWrapper';
import { Select } from '@dealstack/frontend/shared/components/forms/Select/Select';
import {
	InputWrapper,
	StringInput,
} from '@dealstack/frontend/shared/components/forms/StringInput/StringInput';
import { Checkbox } from '@dealstack/frontend/shared/components/fundament/Checkbox/Checkbox';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import React, { BaseSyntheticEvent, memo } from 'react';
import styled, { css } from 'styled-components';

import { Cell, HeaderCell } from './Cell';
import { Row } from './Row';

interface Props {
	className?: string;
	children?: React.ReactNode;
	maxSize?: boolean;
	withEditableRows?: boolean;
	onScroll?: (event: BaseSyntheticEvent) => void;
}

export const RemPerUnitSize = 4;

const DefaultTable = ({ onScroll, className, children }: Props) => {
	return (
		<div onScroll={onScroll} className={className}>
			{children}
		</div>
	);
};

export const HeaderRow = styled.div<{
	sticky?: boolean;
}>`
	${({ sticky }) =>
		sticky
			? `
    position: sticky;
    top: 0px;
    background-color: white;
    `
			: ``}
`;
export const BodyRow = styled.div`
	padding: 0.1em 0 0.2em 0;
`;

const getChildrenSizes = (components: React.ReactElement[]): string => {
	if (!components || !Array.isArray(components)) {
		return '';
	}

	const sizes = components
		.map((component: React.ReactElement | React.ReactElement[]) => {
			if (Array.isArray(component)) {
				return getChildrenSizes(component);
			}

			if (!component?.props) {
				return '';
			}

			if (Array.isArray(component.props.children)) {
				return getChildrenSizes(component.props.children);
			}

			const { size, customSize } = component.props;
			if (customSize) {
				return customSize;
			}
			if (size) {
				return `${size * RemPerUnitSize}rem`;
			}
			return '6rem';
		})
		.join(' ');
	return sizes;
};

function ensureArray<T>(value: T | T[]): T[] {
	if (Array.isArray(value)) {
		return value;
	} else if (value === undefined || value === null) {
		return [];
	} else {
		return [value];
	}
}

const Memoized = memo(DefaultTable);
const Styled = styled(Memoized)`
	display: flex;
	overflow: auto;
	flex-direction: column;

	--grid-col-templates: ${({ children }) => {
		/**
		 * This function takes all header elements and counts how many headers there are.
		 * Then it reads "size" property of them and create proper css grid.
		 */
		const [headerChildren] = ensureArray(children) as React.ReactElement[];
		const sizes = getChildrenSizes(headerChildren?.props?.children);

		return sizes;
	}};

	${HeaderRow} {
		border-bottom: 1px solid ${({ theme }) => theme.v2.color.neutral[300]};
		margin-bottom: 0.5em;
	}
	${HeaderRow}, ${Row} {
		display: grid;
		grid-template-columns: var(--grid-col-templates);
		padding: 0.5em;
		grid-column-gap: 0.2em;
		min-width: fit-content;
	}

	${HeaderCell}, ${Cell} {
		min-width: 1em;
	}
	${HeaderRow}, ${BodyRow} {
		width: ${({ maxSize }) => (maxSize ? 'auto' : 'max-content')};
	}
	${BodyRow} {
		display: flex;
		flex-direction: column;
		${RowWrapper}:not(:last-of-type) {
			margin-bottom: 0.125em;
		}
		${Row}:not(:last-of-type) {
			margin-bottom: 0.125em;
		}
	}

	${Row} {
		border-radius: 4px;
		background-color: ${({ theme }) => theme.v2.color.neutral[100]};
	}

	${({ withEditableRows }) =>
		withEditableRows
			? css`
					${Cell} > *:not(${StringInput}):not(${Select}):not(${Checkbox}):not(${InputWrapper}) {
						margin-left: 0.9em;
						margin-top: 0.1em;
					}

					${HeaderCell} > div > ${Text} {
						margin-left: 0.2em;
					}
				`
			: ''}

	.flat {
		display: flex;
	}
`;

export { Styled as DefaultTable };
